import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import client from 'api/client';
import { setAlert } from '../../../redux/alert';

export interface ConfirmDialogState {
    open: boolean;
    title: string;
    content: string;
    loading: boolean;
    onConfirm: () => void;
}

export interface MessageComposerState {
    open: boolean;
    recipientName: string;
    chatGroupId: number;
    template: string;
}

const initialConfirmDialog: ConfirmDialogState = {
    open: false,
    title: '',
    content: '',
    loading: false,
    onConfirm: () => undefined,
};

const initialMessageComposer: MessageComposerState = {
    open: false,
    recipientName: '',
    chatGroupId: 0,
    template: '',
};

const useAnalyticsActions = (onActionComplete?: () => void) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>(initialConfirmDialog);
    const [messageComposer, setMessageComposer] =
        useState<MessageComposerState>(initialMessageComposer);
    const [reminderCooldowns, setReminderCooldowns] = useState<Set<string>>(new Set());
    const cooldownTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const onActionCompleteRef = useRef(onActionComplete);
    useEffect(() => {
        onActionCompleteRef.current = onActionComplete;
    }, [onActionComplete]);

    // --- Navigation ---

    const navigateToClient = useCallback(
        (clientId: number) => navigate(`/dashboard/${clientId}/client-profile`),
        [navigate]
    );

    const navigateToDocuments = useCallback(
        (clientId: number, appId: number) =>
            navigate(`/dashboard/${clientId}/client-profile/${appId}/documents`),
        [navigate]
    );

    const navigateToContract = useCallback(
        (clientId: number, appId: number) =>
            navigate(`/dashboard/${clientId}/client-profile/agreement/${appId}`),
        [navigate]
    );

    const navigateToChat = useCallback(
        (clientId: number, appId: number) =>
            navigate(`/dashboard/${clientId}/client-profile/${appId}/chat`),
        [navigate]
    );

    // --- Confirmation helpers ---

    const closeConfirmDialog = useCallback(() => setConfirmDialog(initialConfirmDialog), []);

    const showConfirm = useCallback(
        (title: string, content: string, onSuccess: () => Promise<void> | void) => {
            setConfirmDialog({
                open: true,
                title,
                content,
                loading: false,
                onConfirm: async () => {
                    setConfirmDialog((prev) => ({ ...prev, loading: true }));
                    try {
                        await onSuccess();
                    } catch {
                        dispatch(
                            setAlert({ message: 'Action failed. Please try again.', type: 'error' })
                        );
                    } finally {
                        setConfirmDialog(initialConfirmDialog);
                    }
                },
            });
        },
        [dispatch]
    );

    // --- Real API actions ---

    const isReminderCoolingDown = useCallback(
        (appId?: number, docId?: number) => {
            if (!appId || !docId) return false;
            return reminderCooldowns.has(`${appId}:${docId}`);
        },
        [reminderCooldowns]
    );

    const sendReminder = useCallback(
        (candidateName: string, appId?: number, docId?: number) => {
            if (isReminderCoolingDown(appId, docId)) return;
            showConfirm(
                'Send Reminder',
                `Send a document reminder email to ${candidateName}?`,
                async () => {
                    if (appId && docId) {
                        await client.patch(`/document-properties/${appId}/${docId}`, {});
                        const key = `${appId}:${docId}`;
                        setReminderCooldowns((prev) => new Set(prev).add(key));
                        const timer = setTimeout(() => {
                            setReminderCooldowns((prev) => {
                                const next = new Set(prev);
                                next.delete(key);
                                return next;
                            });
                            cooldownTimers.current.delete(key);
                        }, 60_000);
                        cooldownTimers.current.set(key, timer);
                    }
                    dispatch(
                        setAlert({ message: `Reminder sent to ${candidateName}`, type: 'success' })
                    );
                    onActionCompleteRef.current?.();
                }
            );
        },
        [showConfirm, dispatch, isReminderCoolingDown]
    );

    const resendInvoice = useCallback(
        (candidateName: string, appId?: number, milestoneId?: number) => {
            showConfirm('Resend Invoice', `Resend the invoice to ${candidateName}?`, async () => {
                if (appId && milestoneId) {
                    await client.get(
                        `/applications/${appId}/milestones/${milestoneId}/email-invoice`
                    );
                }
                dispatch(
                    setAlert({ message: `Invoice resent to ${candidateName}`, type: 'success' })
                );
                onActionCompleteRef.current?.();
            });
        },
        [showConfirm, dispatch]
    );

    const markPaid = useCallback(
        (candidateName: string, appId?: number, milestoneId?: number) => {
            showConfirm(
                'Mark as Paid',
                `Confirm that ${candidateName} has paid in full?`,
                async () => {
                    if (appId && milestoneId) {
                        await client.put(
                            `/applications/${appId}/milestones/${milestoneId}/payment-status`,
                            {
                                payment_status: 'FULLY_PAID',
                            }
                        );
                    }
                    dispatch(
                        setAlert({ message: `${candidateName} marked as paid`, type: 'success' })
                    );
                    onActionCompleteRef.current?.();
                }
            );
        },
        [showConfirm, dispatch]
    );

    // --- Message composer ---

    const openMessageComposer = useCallback(
        (chatGroupId: number, recipientName: string, context?: string) => {
            if (!chatGroupId) return;
            setMessageComposer({
                open: true,
                recipientName,
                chatGroupId,
                template: `Hi ${recipientName}, just following up on ${context || 'your application'}. Please let me know if you need any help.`,
            });
        },
        []
    );

    const closeMessageComposer = useCallback(() => setMessageComposer(initialMessageComposer), []);

    const sendMessage = useCallback(
        async (recipientName: string, messageText?: string) => {
            const { chatGroupId, template } = messageComposer;
            const text = messageText || template;

            try {
                await client.post('/message', {
                    chat_group_id: chatGroupId,
                    text,
                });
                dispatch(
                    setAlert({ message: `Message sent to ${recipientName}`, type: 'success' })
                );
                onActionCompleteRef.current?.();
            } catch {
                dispatch(setAlert({ message: 'Failed to send message', type: 'error' }));
            } finally {
                setMessageComposer(initialMessageComposer);
            }
        },
        [messageComposer, dispatch]
    );

    return {
        // Navigation
        navigateToClient,
        navigateToDocuments,
        navigateToContract,
        navigateToChat,
        // Actions
        sendReminder,
        isReminderCoolingDown,
        resendInvoice,
        markPaid,
        // Message composer
        openMessageComposer,
        closeMessageComposer,
        sendMessage,
        messageComposer,
        // Confirmation dialog
        confirmDialog,
        closeConfirmDialog,
    };
};

export type AnalyticsActions = ReturnType<typeof useAnalyticsActions>;

export default useAnalyticsActions;
